import React from 'react';
import WebProjectsSidebar from '../layout/WebProjectsSidebar';
import WebProfileHeader from '../layout/WebProfileHeader';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { FaExclamationTriangle } from 'react-icons/fa';
import EmployeeMobileNav from '../ui/EmployeeMobileNav';
import SidebarEmployeeWeb from '../layout/SidebarEmployeeWeb';
import NavbarEmployeeWeb from '../layout/NavbarEmployeeWeb';
import { SidebarProvider, useSidebar } from '../../contexts/SidebarContext';
import {
  isWebHost,
  useIsWide,
  WEB_MENU_WIDTH,
  WEB_MENU_WIDTH_COLLAPSED,
  WEB_NAVBAR_HEIGHT,
  WEB_CHAT_CARD_PAD,
  WEB_SIDEBAR_WIDTH,
} from '../../utils/webHost';
import { logoSecura } from '../../assets/assets';

const WebHostShell = ({ children }) => {
  const wide = useIsWide();
  const location = useLocation();
  const { isCollapsed, isOpen, toggleSidebar, toggleSidebarOpen, closeSidebar } = useSidebar();
  const onProjets = location.pathname.startsWith('/projets');
  const onHoraire = location.pathname.startsWith('/horaire');
  /** Card avec marge vs navbar/menu : Projets (liste + chat) ou Schedule (profil + horaires). */
  const useChatCard = wide && (onProjets || onHoraire);
  const menuWidth = isCollapsed ? WEB_MENU_WIDTH_COLLAPSED : WEB_MENU_WIDTH;
  const emptyProjet = wide && location.pathname === '/projets';

  const handleMenuClick = () => {
    toggleSidebarOpen();
    toggleSidebar();
  };

  return (
    <div className="min-h-screen bg-[#f6f7fa]">
      <SidebarEmployeeWeb
        isOpen={isOpen}
        onClose={closeSidebar}
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleSidebar}
      />
      <div className={`transition-all duration-300 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <NavbarEmployeeWeb onMenuClick={handleMenuClick} isCollapsed={isCollapsed} />

        {useChatCard ? (
          <div
            className="fixed right-0 bottom-0 z-10"
            style={{
              top: WEB_NAVBAR_HEIGHT,
              left: menuWidth,
              padding: WEB_CHAT_CARD_PAD,
            }}
          >
            <div className="h-full w-full flex bg-white border border-gray-100 shadow-[0_1.6px_8px_rgba(40,35,45,0.1)] overflow-hidden">
              {onHoraire ? (
                /* Schedule : profil + horaires (zone sous le profil, colonne type liste) */
                <>
                  <div
                    className="flex flex-col h-full shrink-0 border-r border-gray-200 bg-[#f7f8fa] min-h-0"
                    style={{ width: WEB_SIDEBAR_WIDTH }}
                  >
                    <WebProfileHeader />
                    <div className="relative flex-1 min-h-0 overflow-hidden">{children}</div>
                  </div>
                  <div className="flex-1 min-w-0 bg-white" aria-hidden />
                </>
              ) : (
                <>
                  <WebProjectsSidebar embedded />
                  <div className="flex-1 min-w-0 min-h-0 flex flex-col bg-white overflow-hidden">
                    {emptyProjet ? (
                      <div className="flex-1 flex flex-col items-center justify-center text-center px-8 bg-[#fafbfc]">
                        <img src={logoSecura} alt="Secura" className="h-16 w-auto mb-4 object-contain" />
                        <p className="mt-2 text-sm text-gray-500 max-w-sm">
                          Choisissez un projet à gauche pour ouvrir sa conversation. Glissez vos fichiers de l’ordinateur
                          directement dans la discussion.
                        </p>
                      </div>
                    ) : (
                      children
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            {onProjets ? <WebProjectsSidebar menuOffset={menuWidth} /> : null}
            {children}
          </>
        )}
      </div>
    </div>
  );
};

const EmployeeProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const excludedPaths = [
    '/pointageEntree',
    '/pointageSortie',
    '/pointageEnregistreeEntre',
    '/pointageEnregistreeSortie',
    '/home',
    '/register',
    '/password-fallback',
    '/login-webauthn',
    '/login',
    '/LocationActivation',
    '/activate-location',
    '/quick-checkin',
    '/reset-password',
  ];

  const isProjectConversation =
    location.pathname.startsWith('/projets/') && searchParams.get('tab') === 'conversation';
  const shouldShowNav =
    !excludedPaths.some((path) => location.pathname.startsWith(path)) && !isProjectConversation;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Vérification des permissions employé...</p>
        </div>
      </div>
    );
  }

  const authed = typeof isAuthenticated === 'function' ? isAuthenticated() : !!isAuthenticated;
  if (!authed) {
    return <Navigate to="/login" replace />;
  }

  const isEmployee = user?.role?.nom === 'Employee' || user?.role === 'Employee';
  if (!isEmployee) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaExclamationTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Accès Refusé</h2>
          <p className="text-gray-600 mb-6">
            Vous n&apos;avez pas les permissions nécessaires pour accéder à cette interface employé. Seuls les
            employés peuvent accéder à cette section.
          </p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-700">
              <strong>Votre rôle actuel:</strong> {user?.role?.nom || user?.role || 'Non défini'}
            </p>
            <p className="text-sm text-red-700 mt-1">
              <strong>Rôle requis:</strong> Employee
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isWebHost()) {
    return (
      <SidebarProvider>
        <WebHostShell>{children}</WebHostShell>
      </SidebarProvider>
    );
  }

  return (
    <>
      {children}
      {shouldShowNav ? <EmployeeMobileNav /> : null}
    </>
  );
};

export default EmployeeProtectedRoute;
